import { useEffect, useState } from 'react'
import { FileText, Globe } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/Text'
import { knowledgeApi } from '@/lib/api'
import { cn } from '@/lib/utils'

type PreviewMode = 'web' | 'text'

const HTML_EXTENSIONS: Array<string> = ['.html', '.htm', '.xhtml']

interface HtmlDocumentPreviewProps {
  documentToken: string
  title: string
  extractedText?: string | null
  className?: string
}

export function isHtmlDocument(
  mimeType?: string | null,
  filename?: string | null,
): boolean {
  if ((mimeType || '').toLowerCase().includes('html')) {
    return true
  }
  const name = (filename || '').toLowerCase()
  return HTML_EXTENSIONS.some((extension) => name.endsWith(extension))
}

/**
 * Uploaded HTML rendered as a page instead of as extracted text.
 *
 * The markup is untrusted third-party content, so it is fed to the frame via
 * `srcDoc` under a fully restrictive `sandbox`: no scripts, and an opaque
 * origin so the embedded page cannot reach our DOM or storage. Stylesheets and
 * images still load, which is what makes it look like the original site.
 */
export function HtmlDocumentPreview(props: HtmlDocumentPreviewProps) {
  const { documentToken, title, extractedText, className } = props

  const [html, setHtml] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [mode, setMode] = useState<PreviewMode>('web')

  useEffect(() => {
    let active = true
    setHtml(null)
    setLoadFailed(false)

    knowledgeApi
      .getDocumentHtml(documentToken)
      .then((markup) => {
        if (active) {
          setHtml(markup)
        }
      })
      .catch(() => {
        if (active) {
          setLoadFailed(true)
        }
      })

    return () => {
      active = false
    }
  }, [documentToken])

  const showText = mode === 'text' || loadFailed

  return (
    <div
      className={cn('flex min-h-0 flex-1 flex-col gap-3', className)}
      data-testid="html-document-preview"
    >
      <div className="flex shrink-0 items-center justify-between gap-3">
        <Text size="xs" color="muted">
          {showText
            ? 'Text as the model reads it'
            : 'Original page, scripts disabled'}
        </Text>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant={showText ? 'ghost' : 'outline'}
            size="sm"
            className="h-8"
            disabled={loadFailed}
            data-testid="html-document-mode-web"
            onClick={() => setMode('web')}
          >
            <Globe className="mr-1.5 h-3.5 w-3.5" />
            Web view
          </Button>
          <Button
            type="button"
            variant={showText ? 'outline' : 'ghost'}
            size="sm"
            className="h-8"
            data-testid="html-document-mode-text"
            onClick={() => setMode('text')}
          >
            <FileText className="mr-1.5 h-3.5 w-3.5" />
            Extracted text
          </Button>
        </div>
      </div>

      {showText ? (
        <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-border bg-white p-6 text-black shadow-sm dark:bg-zinc-100">
          {loadFailed ? (
            <Text size="sm" className="mb-4 text-amber-700">
              The original file could not be loaded, so this is the extracted
              text.
            </Text>
          ) : null}
          <pre
            className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-zinc-800"
            data-testid="html-document-text"
          >
            {extractedText || 'No text extracted for this document.'}
          </pre>
        </div>
      ) : html == null ? (
        <div className="min-h-0 flex-1 space-y-3 rounded-xl border border-border bg-white p-6 dark:bg-zinc-100">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <iframe
          title={title}
          srcDoc={html}
          className="min-h-[70vh] w-full flex-1 rounded-xl border border-border bg-white"
          sandbox=""
          referrerPolicy="no-referrer"
          data-testid="html-document-frame"
        />
      )}
    </div>
  )
}
